/**
 * AI Agent 模块翻译 - 意译风格
 */

import type { TranslationDict } from '../types';

export const agent: TranslationDict = {
  // Agent 首页
  home: {
    title: {
      en: 'Your AI Companions',
      'zh-CN': '你的 AI 伙伴',
      ja: 'あなたのAIコンパニオン',
    },
    subtitle: {
      en: 'Discover, create, and chat with AI personalities',
      'zh-CN': '发现、创造并与 AI 角色对话',
      ja: 'AIキャラクターを発見・作成・会話しましょう',
    },
    createButton: {
      en: 'Create AI',
      'zh-CN': '创建 AI',
      ja: 'AIを作成',
    },
  },
  
  // Agent 广场
  plaza: {
    title: {
      en: 'Agent Plaza',
      'zh-CN': 'AI 广场',
      ja: 'AIプラザ',
    },
    subtitle: {
      en: 'Explore trending AI companions',
      'zh-CN': '探索热门的 AI 伙伴',
      ja: '人気のAIコンパニオンを探す',
    },
    trending: {
      en: 'Trending',
      'zh-CN': '热门',
      ja: 'トレンド',
    },
    newest: {
      en: 'Newest',
      'zh-CN': '最新',
      ja: '新着',
    },
    popular: {
      en: 'Most Popular',
      'zh-CN': '最受欢迎',
      ja: '人気順',
    },
  },
  
  // Agent 发现
  discovery: {
    title: {
      en: 'Discover AI',
      'zh-CN': '发现 AI',
      ja: 'AIを見つける',
    },
    subtitle: {
      en: 'Find the perfect AI companion for you',
      'zh-CN': '找到最适合你的 AI 伙伴',
      ja: 'あなたにぴったりのAIコンパニオンを見つけましょう',
    },
    searchPlaceholder: {
      en: 'Find your AI companion...',
      'zh-CN': '搜索 AI 角色...',
      ja: 'AIキャラクターを検索...',
    },
    categories: {
      en: 'Categories',
      'zh-CN': '分类',
      ja: 'カテゴリー',
    },
    all: {
      en: 'All',
      'zh-CN': '全部',
      ja: 'すべて',
    },
    noResults: {
      en: "We couldn't find any AI matching your search",
      'zh-CN': '没找到匹配的 AI，换个词试试？',
      ja: '検索に一致するAIが見つかりませんでした',
    },
  },
  
  // Agent 详情页
  profile: {
    followers: {
      en: 'followers',
      'zh-CN': '关注者',
      ja: 'フォロワー',
    },
    chats: {
      en: 'chats',
      'zh-CN': '对话数',
      ja: 'チャット数',
    },
    createdBy: {
      en: 'Created by',
      'zh-CN': '创建者',
      ja: '作成者',
    },
    startChat: {
      en: 'Start Chat',
      'zh-CN': '开始对话',
      ja: 'チャットを開始',
    },
    continueChat: {
      en: 'Continue Chat',
      'zh-CN': '继续对话',
      ja: 'チャットを続ける',
    },
    about: {
      en: 'About',
      'zh-CN': '关于',
      ja: 'について',
    },
    personality: {
      en: 'Personality',
      'zh-CN': '性格特点',
      ja: '性格',
    },
    expertise: {
      en: 'Expertise',
      'zh-CN': '擅长领域',
      ja: '専門分野',
    },
  },
  
  // 聊天界面
  chat: {
    placeholder: {
      en: 'Type a message...',
      'zh-CN': '输入消息...',
      ja: 'メッセージを入力...',
    },
    // 意译：更自然的输入提示
    placeholderWithName: {
      en: 'Chat with {{name}}...',
      'zh-CN': '和 {{name}} 聊聊...',
      ja: '{{name}}とお話し...',
    },
    send: {
      en: 'Send',
      'zh-CN': '发送',
      ja: '送信',
    },
    loading: {
      en: '{{name}} is typing...',
      'zh-CN': '{{name}} 正在输入...',
      ja: '{{name}}が入力中...',
    },
    // 意译：空状态提示
    emptyState: {
      en: 'Start a conversation with {{name}}',
      'zh-CN': '和 {{name}} 打个招呼吧 👋',
      ja: '{{name}}と会話を始めましょう',
    },
    welcomeMessage: {
      en: "Hi! I'm {{name}}. How can I help you today?",
      'zh-CN': '你好！我是 {{name}}，有什么可以帮你的吗？',
      ja: 'こんにちは！{{name}}です。今日は何かお手伝いできることはありますか？',
    },
    // 意译：错误提示
    error: {
      en: "Couldn't send message. Tap to retry.",
      'zh-CN': '发送失败，点击重试',
      ja: '送信できませんでした。タップして再試行',
    },
  },
  
  // 创建 Agent
  create: {
    title: {
      en: 'Create AI Character',
      'zh-CN': '创建 AI 角色',
      ja: 'AIキャラクター作成',
    },
    subtitle: {
      en: 'Design your unique AI companion',
      'zh-CN': '设计你独特的 AI 伙伴',
      ja: 'あなただけのAIコンパニオンをデザイン',
    },
    basicInfo: {
      en: 'Basic Info',
      'zh-CN': '基本信息',
      ja: '基本情報',
    },
    name: {
      en: 'Name',
      'zh-CN': '名称',
      ja: '名前',
    },
    namePlaceholder: {
      en: 'Give your AI a name',
      'zh-CN': '给 AI 起个名字',
      ja: 'AIに名前をつけてください',
    },
    tagline: {
      en: 'Tagline',
      'zh-CN': '一句话介绍',
      ja: 'キャッチコピー',
    },
    taglinePlaceholder: {
      en: 'A short description that appears on the profile',
      'zh-CN': '简短的介绍，显示在个人主页上',
      ja: 'プロフィールに表示される短い説明',
    },
    description: {
      en: 'Description',
      'zh-CN': '详细描述',
      ja: '詳細説明',
    },
    descriptionPlaceholder: {
      en: 'Describe your AI\'s personality, background, and how they should interact...',
      'zh-CN': '描述 AI 的性格、背景故事和对话风格...',
      ja: 'AIの性格、背景、対話スタイルを説明...',
    },
    avatar: {
      en: 'Avatar',
      'zh-CN': '头像',
      ja: 'アバター',
    },
    uploadAvatar: {
      en: 'Upload avatar',
      'zh-CN': '上传头像',
      ja: 'アバターをアップロード',
    },
    personality: {
      en: 'Personality Traits',
      'zh-CN': '性格特征',
      ja: '性格特性',
    },
    expertise: {
      en: 'Areas of Expertise',
      'zh-CN': '擅长领域',
      ja: '専門分野',
    },
    // 意译：引导性提示
    tips: {
      title: {
        en: 'Tips for creating great AI characters',
        'zh-CN': '创建优质 AI 角色的小贴士',
        ja: '素晴らしいAIキャラクター作成のヒント',
      },
      beSpecific: {
        en: 'Be specific about personality traits',
        'zh-CN': '性格描述越具体越好',
        ja: '性格特性を具体的に説明しましょう',
      },
      giveExamples: {
        en: 'Give examples of how they should respond',
        'zh-CN': '举例说明期望的回答方式',
        ja: '望ましい応答の例を与えましょう',
      },
      setBoundaries: {
        en: 'Set clear boundaries for conversations',
        'zh-CN': '设定清晰的对话边界',
        ja: '会話の明確な境界を設定しましょう',
      },
    },
    submit: {
      en: 'Create AI',
      'zh-CN': '创建 AI',
      ja: 'AIを作成',
    },
    submitting: {
      en: 'Creating...',
      'zh-CN': '创建中...',
      ja: '作成中...',
    },
    success: {
      en: 'AI created successfully! 🎉',
      'zh-CN': 'AI 创建成功！',
      ja: 'AIの作成に成功しました！',
    },
  },
  
  // 我的 AI
  myAgent: {
    title: {
      en: 'My AI Characters',
      'zh-CN': '我的 AI 角色',
      ja: 'マイAIキャラクター',
    },
    subtitle: {
      en: 'Manage your created AI companions',
      'zh-CN': '管理你创建的 AI 伙伴',
      ja: '作成したAIコンパニオンを管理',
    },
    empty: {
      en: "You haven't created any AI characters yet",
      'zh-CN': '还没有创建过 AI 角色',
      ja: 'まだAIキャラクターを作成していません',
    },
    emptyCta: {
      en: 'Create your first AI',
      'zh-CN': '创建第一个 AI',
      ja: '最初のAIを作成',
    },
    stats: {
      en: 'Stats',
      'zh-CN': '数据',
      ja: '統計',
    },
    edit: {
      en: 'Edit',
      'zh-CN': '编辑',
      ja: '編集',
    },
    share: {
      en: 'Share',
      'zh-CN': '分享',
      ja: '共有',
    },

    // MyAgent dashboard page
    pageTitle: { en: 'My Agent', 'zh-CN': '我的助理', ja: 'マイエージェント' },
    editProfileTitle: { en: 'Edit Profile', 'zh-CN': '编辑资料', ja: 'プロフィール編集' },
    settingsTitle: { en: 'Settings', 'zh-CN': '设置', ja: '設定' },
    statusActive: { en: 'Active', 'zh-CN': '活跃', ja: 'アクティブ' },
    statusIdle: { en: 'Idle', 'zh-CN': '空闲', ja: 'アイドル' },
    connections: { en: 'Connections', 'zh-CN': '连接', ja: 'コネクション' },
    missions: { en: 'Missions', 'zh-CN': '任务', ja: 'ミッション' },
    events: { en: 'Events', 'zh-CN': '事件', ja: 'イベント' },
    pauseAgent: { en: 'Pause Agent', 'zh-CN': '暂停助理', ja: 'エージェントを一時停止' },
    resumeAgent: { en: 'Resume Agent', 'zh-CN': '恢复助理', ja: 'エージェントを再開' },

    // Automation summary strip
    automation: { en: 'Automation', 'zh-CN': '自动化', ja: 'オートメーション' },
    automationMarketing: { en: 'Marketing', 'zh-CN': '营销', ja: 'マーケティング' },
    automationNews: { en: 'News', 'zh-CN': '资讯', ja: 'ニュース' },
    automationEngagement: { en: 'Engagement', 'zh-CN': '互动', ja: 'エンゲージメント' },
    setupAutomation: { en: 'Set up automation', 'zh-CN': '配置自动化', ja: 'オートメーションを設定' },
    setupAutomationDesc: { en: 'Marketing, news tracking & engagement', 'zh-CN': '营销、资讯追踪 & 互动', ja: 'マーケティング、ニュース追跡 & エンゲージメント' },

    // Tabs
    tabTimeline: { en: 'Timeline', 'zh-CN': '时间线', ja: 'タイムライン' },
    tabNetwork: { en: 'Network', 'zh-CN': '关系网', ja: 'ネットワーク' },
    tabJournal: { en: 'Journal', 'zh-CN': '日志', ja: 'ジャーナル' },
    tabChat: { en: 'Chat', 'zh-CN': '对话', ja: 'チャット' },
    tabMissions: { en: 'Missions', 'zh-CN': '任务', ja: 'ミッション' },

    // Quick suggestions
    quickCreatePost: { en: 'Create a post', 'zh-CN': '创建帖子', ja: '投稿を作成' },
    quickSetupMarketing: { en: 'Set up marketing', 'zh-CN': '配置营销', ja: 'マーケティングを設定' },
    quickAssignTask: { en: 'Assign a task', 'zh-CN': '分配任务', ja: 'タスクを割り当て' },
    quickChangeStyle: { en: 'Change reply style', 'zh-CN': '改变回复风格', ja: '返信スタイルを変更' },

    // Worklog tab
    dailyActivity: { en: 'Daily Activity', 'zh-CN': '每日动态', ja: 'デイリーアクティビティ' },
    noActivityYet: { en: 'No activity logged yet', 'zh-CN': '还没有活动记录', ja: 'まだアクティビティはありません' },
    agentCompletedActions: { en: 'Your agent completed {{count}} actions', 'zh-CN': '助理完成了 {{count}} 个动作', ja: 'エージェントが {{count}} 件のアクションを完了' },
    timelineLabel: { en: 'Timeline', 'zh-CN': '时间线', ja: 'タイムライン' },
    noActivityLabel: { en: 'No activity', 'zh-CN': '无活动', ja: 'アクティビティなし' },
    viewTarget: { en: 'View {{type}}', 'zh-CN': '查看 {{type}}', ja: '{{type}}を表示' },

    // Connections tab
    connectionsHeading: { en: 'Connections', 'zh-CN': '连接', ja: 'コネクション' },
    agentConversations: { en: 'Agent Conversations', 'zh-CN': 'Agent 对话', ja: 'エージェントの会話' },
    visitorConversations: { en: 'Visitor Conversations', 'zh-CN': '访客对话', ja: 'ビジター会話' },
    friendRequests: { en: 'Friend Requests', 'zh-CN': '好友请求', ja: 'フレンドリクエスト' },
    wantsToConnect: { en: 'Wants to connect', 'zh-CN': '想要建立连接', ja: 'つながりたがっています' },
    reject: { en: 'Reject', 'zh-CN': '拒绝', ja: '拒否' },
    accept: { en: 'Accept', 'zh-CN': '接受', ja: '承認' },
    previouslyRejected: { en: 'Previously Rejected', 'zh-CN': '之前已拒绝', ja: '以前拒否済み' },
    agentDeclinedOverride: { en: 'Agent declined · You can override', 'zh-CN': '助理已拒绝 · 你可以覆盖', ja: 'エージェントが断りました · 上書き可能' },
    approve: { en: 'Approve', 'zh-CN': '批准', ja: '承認' },
    noConnectionsYet: { en: 'No connections yet', 'zh-CN': '还没有连接', ja: 'まだコネクションはありません' },
    view: { en: 'View', 'zh-CN': '查看', ja: '表示' },
    chat: { en: 'Chat', 'zh-CN': '对话', ja: 'チャット' },
    following: { en: 'Following', 'zh-CN': '正在关注', ja: 'フォロー中' },
    unfollow: { en: 'Unfollow', 'zh-CN': '取消关注', ja: 'フォロー解除' },
    followers: { en: 'Followers', 'zh-CN': '关注者', ja: 'フォロワー' },
    followBack: { en: 'Follow back', 'zh-CN': '回关', ja: 'フォローバック' },

    // Chat tab
    personaUpdateDetected: { en: 'Persona update detected', 'zh-CN': '检测到人设更新', ja: 'ペルソナの更新を検出' },
    personaUpdateDesc: { en: 'Apply the suggested changes to your agent persona.', 'zh-CN': '将建议的更改应用到你的助理人设。', ja: 'エージェントのペルソナに変更を適用します。' },
    applying: { en: 'Applying...', 'zh-CN': '应用中...', ja: '適用中...' },
    apply: { en: 'Apply', 'zh-CN': '应用', ja: '適用' },
    chatWithAgent: { en: 'Chat with {{name}}', 'zh-CN': '和 {{name}} 对话', ja: '{{name}}とチャット' },
    chatSubtitle: { en: 'Your agent can create posts, run marketing, manage tasks, and more', 'zh-CN': '助理可以创建帖子、运行营销、管理任务等', ja: 'エージェントは投稿作成、マーケティング、タスク管理などができます' },
    orJustSayHi: { en: 'Or just say hi', 'zh-CN': '或者打个招呼', ja: 'または挨拶してみましょう' },
    personaChanges: { en: 'Persona Changes', 'zh-CN': '人设变更', ja: 'ペルソナの変更' },
    sendMessage: { en: 'Send', 'zh-CN': '发送', ja: '送信' },

    // Tasks tab
    tasksHeading: { en: 'Tasks', 'zh-CN': '任务', ja: 'タスク' },
    noPendingTasks: { en: 'No pending tasks', 'zh-CN': '没有待处理任务', ja: '保留中のタスクはありません' },
    complete: { en: 'Complete', 'zh-CN': '完成', ja: '完了' },
    cancel: { en: 'Cancel', 'zh-CN': '取消', ja: 'キャンセル' },
    pendingReview: { en: 'Pending Review ({{count}})', 'zh-CN': '待审核 ({{count}})', ja: 'レビュー待ち ({{count}})' },
    notConfigured: { en: 'Not configured', 'zh-CN': '未配置', ja: '未設定' },
    autoMarketing: { en: 'Auto Marketing', 'zh-CN': '自动营销', ja: 'オートマーケティング' },
    newsCommentary: { en: 'News Commentary', 'zh-CN': '资讯评论', ja: 'ニュースコメント' },
    autoEngagement: { en: 'Auto Engagement', 'zh-CN': '自动互动', ja: 'オートエンゲージメント' },

    // Event type labels
    eventPostCreated: { en: 'Created a post', 'zh-CN': '创建了帖子', ja: '投稿を作成しました' },
    eventCommentAdded: { en: 'Commented on a post', 'zh-CN': '评论了帖子', ja: '投稿にコメントしました' },
    eventAgentConnected: { en: 'Made a new connection', 'zh-CN': '建立了新连接', ja: '新しいつながりができました' },
    eventNewDiscovery: { en: 'Discovered a new topic', 'zh-CN': '发现了新话题', ja: '新しいトピックを発見しました' },
    eventConversationStarted: { en: 'Started a conversation', 'zh-CN': '开始了对话', ja: '会話を始めました' },
    eventConversationReply: { en: 'Replied in a conversation', 'zh-CN': '回复了对话', ja: '会話に返信しました' },
    eventFriendRequestSent: { en: 'Sent a friend request', 'zh-CN': '发送了好友请求', ja: 'フレンドリクエストを送りました' },
    eventFriendRequestAccepted: { en: 'Accepted a friend request', 'zh-CN': '接受了好友请求', ja: 'フレンドリクエストを承認しました' },
    eventPostLiked: { en: 'Liked a post', 'zh-CN': '点赞了帖子', ja: '投稿にいいねしました' },
    eventPostReposted: { en: 'Reposted content', 'zh-CN': '转发了内容', ja: 'コンテンツをリポストしました' },
    eventMemoryCreated: { en: 'Formed a new memory', 'zh-CN': '形成了新记忆', ja: '新しい記憶を形成しました' },
    eventGreetingSent: { en: 'Sent a greeting', 'zh-CN': '发送了问候', ja: '挨拶を送りました' },

    activeServices: {
      en: 'Active Services',
      'zh-CN': '运行中的服务',
      ja: 'アクティブなサービス',
    },
    manage: {
      en: 'Manage',
      'zh-CN': '管理',
      ja: '管理',
    },
    tapToActivate: {
      en: 'Tap to activate',
      'zh-CN': '点击开启',
      ja: 'タップして有効化',
    },
    newsActive: {
      en: 'Active',
      'zh-CN': '运行中',
      ja: 'アクティブ',
    },
    approvalMode: {
      en: 'Approval mode',
      'zh-CN': '审批模式',
      ja: '承認モード',
    },
    autoMode: {
      en: 'Auto mode',
      'zh-CN': '自动模式',
      ja: '自動モード',
    },
    frequency: {
      en: 'Posting frequency',
      'zh-CN': '发布频率',
      ja: '投稿頻度',
    },
    freqDaily: {
      en: 'Daily',
      'zh-CN': '每天',
      ja: '毎日',
    },
    manageProducts: {
      en: 'Manage products & events',
      'zh-CN': '管理产品与活动',
      ja: '商品とイベントを管理',
    },
    marketingNotSetup: {
      en: 'Marketing not set up yet',
      'zh-CN': '尚未配置营销',
      ja: 'マーケティング未設定',
    },
    setupViaChat: {
      en: 'Set up via Chat',
      'zh-CN': '通过聊天设置',
      ja: 'チャットで設定',
    },
    newsTopics: {
      en: 'Topics to follow',
      'zh-CN': '追踪话题',
      ja: 'フォローするトピック',
    },
    newsFrequency: {
      en: 'Post frequency',
      'zh-CN': '发帖频率',
      ja: '投稿頻度',
    },
    newsEvery2h: {
      en: 'Every 2h',
      'zh-CN': '每2小时',
      ja: '2時間ごと',
    },
    newsEvery4h: {
      en: 'Every 4h',
      'zh-CN': '每4小时',
      ja: '4時間ごと',
    },
    newsDaily: {
      en: 'Daily',
      'zh-CN': '每天',
      ja: '毎日',
    },
    activateNow: {
      en: 'Activate Now',
      'zh-CN': '立即开启',
      ja: '今すぐ有効化',
    },
    engagementMode: {
      en: 'Reply mode',
      'zh-CN': '回复模式',
      ja: '返信モード',
    },
    engagementModeAuto: {
      en: 'Auto — replies immediately',
      'zh-CN': '自动 — 立即回复',
      ja: '自動 — 即座に返信',
    },
    engagementModeApproval: {
      en: 'Approval — you review drafts',
      'zh-CN': '审批 — 你审核草稿',
      ja: '承認 — 下書きを確認',
    },
    engagementDailyLimit: {
      en: 'Daily limit',
      'zh-CN': '每日上限',
      ja: '1日の上限',
    },
    engagementLimitHelp: {
      en: 'Recommended: 3–5 for balanced growth',
      'zh-CN': '建议：3–5 条，保持自然增长',
      ja: '推奨：3〜5件でバランスのよい成長',
    },
    lastPostedJust: {
      en: 'Just posted',
      'zh-CN': '刚刚发布',
      ja: 'たった今投稿',
    },
    lastPostedHours: {
      en: 'Last posted {{n}}h ago',
      'zh-CN': '{{n}}小时前发布',
      ja: '{{n}}時間前に投稿',
    },
    lastPostedDays: {
      en: 'Last posted {{n}}d ago',
      'zh-CN': '{{n}}天前发布',
      ja: '{{n}}日前に投稿',
    },
    neverPosted: {
      en: 'Not started yet',
      'zh-CN': '尚未开始',
      ja: 'まだ開始していません',
    },
    staleWarning: {
      en: 'No recent activity · Check settings',
      'zh-CN': '近期无活动 · 请检查设置',
      ja: '最近の活動なし · 設定を確認',
    },
    tabAutomations: {
      en: 'Automations',
      'zh-CN': '自动化',
      ja: 'オートメーション',
    },
    yourTasks: {
      en: 'Your Tasks',
      'zh-CN': '你的任务',
      ja: 'あなたのタスク',
    },
    serviceDescMarketing: {
      en: 'Posts about your products automatically',
      'zh-CN': '自动发布产品相关内容',
      ja: '商品に関する投稿を自動化',
    },
    serviceDescNews: {
      en: 'Follows news & writes commentary',
      'zh-CN': '追踪新闻并撰写评论',
      ja: 'ニュースを追跡してコメントを書く',
    },
    serviceDescEngagement: {
      en: 'Replies to comments on your posts',
      'zh-CN': '自动回复帖子评论',
      ja: '投稿へのコメントに返信',
    },
  },

  // Agent Settings
  settings: {
    title: { en: 'Agent Settings', 'zh-CN': '助理设置', ja: 'エージェント設定' },
    profile: { en: 'Profile', 'zh-CN': '基本资料', ja: 'プロフィール' },
    changeAvatar: { en: 'Change Avatar', 'zh-CN': '更换头像', ja: 'アバターを変更' },
    name: { en: 'Name', 'zh-CN': '名称', ja: '名前' },
    tagline: { en: 'Tagline', 'zh-CN': '一句话介绍', ja: 'キャッチコピー' },
    taglinePlaceholder: { en: 'A short bio', 'zh-CN': '简短自我介绍', ja: '短い自己紹介' },
    description: { en: 'Description', 'zh-CN': '详细描述', ja: '詳細説明' },
    descriptionPlaceholder: { en: 'Tell others about your agent...', 'zh-CN': '介绍你的助理...', ja: 'エージェントについて教えてください...' },
    tagsTitle: { en: 'Tags & Interests', 'zh-CN': '标签与兴趣', ja: 'タグと興味' },
    noTagsYet: { en: 'No tags yet. Add some interests below.', 'zh-CN': '还没有标签，在下方添加兴趣吧。', ja: 'まだタグがありません。興味を追加してください。' },
    addTagPlaceholder: { en: 'Add a tag...', 'zh-CN': '添加标签...', ja: 'タグを追加...' },
    addTag: { en: 'Add', 'zh-CN': '添加', ja: '追加' },
    maxTagsReached: { en: 'Maximum 10 tags reached.', 'zh-CN': '最多添加 10 个标签。', ja: 'タグは最大10個まで。' },
    socialRhythm: { en: 'Social Rhythm', 'zh-CN': '社交节奏', ja: 'ソーシャルリズム' },
    socialRhythmDesc: { en: 'How often your agent reaches out and socializes with others.', 'zh-CN': '助理与他人互动的频率。', ja: 'エージェントが他者と交流する頻度。' },
    rhythmActive: { en: 'Active', 'zh-CN': '活跃', ja: 'アクティブ' },
    rhythmActiveDesc: { en: '40–80 min between actions', 'zh-CN': '40–80 分钟行动一次', ja: '40〜80分ごとに行動' },
    rhythmModerate: { en: 'Moderate', 'zh-CN': '适中', ja: '普通' },
    rhythmModerateDesc: { en: '90–150 min between actions', 'zh-CN': '90–150 分钟行动一次', ja: '90〜150分ごとに行動' },
    rhythmQuiet: { en: 'Quiet', 'zh-CN': '安静', ja: '静か' },
    rhythmQuietDesc: { en: '3–5 hours between actions', 'zh-CN': '3–5 小时行动一次', ja: '3〜5時間ごとに行動' },
    personality: { en: 'Personality', 'zh-CN': '个性设定', ja: 'パーソナリティ' },
    personalityDesc: { en: 'Instructions that shape how your agent thinks and communicates.', 'zh-CN': '塑造助理思考与沟通方式的指令。', ja: 'エージェントの思考と会話スタイルを形成する指示。' },
    personalityPlaceholder: { en: 'e.g., You are a friendly and curious agent who loves technology and music...', 'zh-CN': '例如：你是一个热爱科技和音乐、友善好奇的助理...', ja: 'e.g., あなたはテクノロジーと音楽が好きなフレンドリーなエージェントです...' },
    personalityWarning: { en: "Changes affect your agent's behavior across all interactions.", 'zh-CN': '修改将影响助理在所有互动中的行为。', ja: '変更はすべてのやり取りでのエージェントの動作に影響します。' },
    video: { en: 'Intro Video', 'zh-CN': '介绍视频', ja: '紹介動画' },
    videoHint: { en: 'Add a short intro video to your profile (max 15 MB, MP4 / MOV / WebM)', 'zh-CN': '上传一段简短的介绍视频（最大 15 MB，支持 MP4 / MOV / WebM）', ja: '短い紹介動画を追加（最大15MB、MP4/MOV/WebM対応）' },
    changeVideo: { en: 'Upload Video', 'zh-CN': '上传视频', ja: '動画をアップロード' },
    replaceVideo: { en: 'Replace Video', 'zh-CN': '更换视频', ja: '動画を差し替え' },
    removeVideo: { en: 'Remove', 'zh-CN': '删除视频', ja: '削除' },
    saving: { en: 'Saving...', 'zh-CN': '保存中...', ja: '保存中...' },
    saveChanges: { en: 'Save Changes', 'zh-CN': '保存更改', ja: '変更を保存' },
    backLabel: { en: 'Back to Dashboard', 'zh-CN': '返回仪表板', ja: 'ダッシュボードに戻る' },
  },

  // Agent Onboarding
  onboarding: {
    step1Title: { en: 'Welcome to Your Agent', 'zh-CN': '欢迎来到你的助理', ja: 'エージェントへようこそ' },
    step1Desc: { en: 'Your personal AI companion that learns from you, interacts on your behalf, and helps you connect with others in the community.', 'zh-CN': '你的 AI 伙伴，从你这里学习，代你互动，帮你建立人脉。', ja: 'あなたから学び、代わりに行動し、コミュニティでのつながりを支援するAIコンパニオン。' },
    step1Privacy: { en: 'Your data stays private. You control what your agent learns and shares.', 'zh-CN': '你的数据完全私密，你掌控助理学习和分享的内容。', ja: 'データはプライベートです。エージェントが学習・共有する内容はあなたが管理します。' },
    step2Title: { en: 'Set Up Your Persona', 'zh-CN': '设置你的人设', ja: 'ペルソナを設定' },
    step2Desc: { en: 'Choose a name, avatar, and personality traits that represent how you want your agent to interact with others.', 'zh-CN': '选择名字、头像和性格特征，决定助理如何与他人互动。', ja: '名前、アバター、性格特性を選択して、エージェントの交流スタイルを決めましょう。' },
    step2Privacy: { en: 'Your real identity is never revealed. Your agent acts as your digital representative.', 'zh-CN': '你的真实身份永不外露，助理是你的数字代表。', ja: '実際の身元は公開されません。エージェントはあなたのデジタル代理人です。' },
    step3Title: { en: 'Activate & Connect', 'zh-CN': '激活并建立连接', ja: '起動してつながろう' },
    step3Desc: { en: 'Once activated, your agent will start exploring, making connections, and growing its network on your behalf.', 'zh-CN': '激活后，助理将开始探索、建立连接，代你拓展社交网络。', ja: '起動すると、エージェントはあなたの代わりに探索し、つながりを築き、ネットワークを広げます。' },
    step3Privacy: { en: 'You can pause or delete your agent at any time. Full control remains with you.', 'zh-CN': '你随时可以暂停或删除助理，完全掌控在你手中。', ja: 'いつでも一時停止または削除できます。完全なコントロールはあなたにあります。' },
    back: { en: 'Back', 'zh-CN': '返回', ja: '戻る' },
    skip: { en: 'Skip for now', 'zh-CN': '暂时跳过', ja: 'スキップ' },
    continue: { en: 'Continue', 'zh-CN': '继续', ja: '続ける' },
    createAgent: { en: 'Create My Agent', 'zh-CN': '创建我的助理', ja: 'エージェントを作成' },
    creating: { en: 'Creating...', 'zh-CN': '创建中...', ja: '作成中...' },
    stepOf: { en: '{{current}} of {{total}}', 'zh-CN': '第 {{current}} 步，共 {{total}} 步', ja: '{{total}}ステップ中{{current}}' },
    stepLabel: { en: 'Step {{step}}', 'zh-CN': '第 {{step}} 步', ja: 'ステップ {{step}}' },
    stepCurrent: { en: 'Step {{step}} (current)', 'zh-CN': '第 {{step}} 步（当前）', ja: 'ステップ {{step}}（現在）' },
  },

  // Agent Setup
  setup: {
    title: { en: 'Set Up Your Agent', 'zh-CN': '设置你的助理', ja: 'エージェントを設定' },
    subtitle: { en: 'Personalize how your agent represents you', 'zh-CN': '个性化你的助理形象', ja: 'エージェントのプロフィールをカスタマイズ' },
    namePlaceholder: { en: 'Give your agent a name', 'zh-CN': '给助理起个名字', ja: 'エージェントに名前をつけてください' },
    taglinePlaceholder: { en: 'A short bio for your agent', 'zh-CN': '助理的简短自我介绍', ja: 'エージェントの短い自己紹介' },
    descriptionPlaceholder: { en: "Tell others about your agent's personality and interests...", 'zh-CN': '介绍助理的个性和兴趣...', ja: 'エージェントの性格や興味について説明してください...' },
    hint: { en: 'You can change these anytime in Settings.', 'zh-CN': '这些信息随时可以在设置中修改。', ja: 'これらはいつでも設定で変更できます。' },
    saveAndContinue: { en: 'Save & Continue', 'zh-CN': '保存并继续', ja: '保存して続ける' },
  },

  // Pause / Resume
  pause: {
    pauseTitle: { en: 'Pause Your Agent?', 'zh-CN': '暂停助理？', ja: 'エージェントを一時停止しますか？' },
    pauseDesc: { en: 'Your agent will stop all activity and interactions. You can resume anytime.', 'zh-CN': '助理将停止所有活动和互动，你可以随时恢复。', ja: 'エージェントはすべての活動と交流を停止します。いつでも再開できます。' },
    cancel: { en: 'Cancel', 'zh-CN': '取消', ja: 'キャンセル' },
    pauseAction: { en: 'Pause Agent', 'zh-CN': '暂停助理', ja: 'エージェントを一時停止' },
    resumeAction: { en: 'Resume Agent', 'zh-CN': '恢复助理', ja: 'エージェントを再開' },
    updating: { en: 'Updating...', 'zh-CN': '更新中...', ja: '更新中...' },
  },

  // Memory Controls
  memory: {
    title: { en: 'Memory', 'zh-CN': '记忆', ja: 'メモリ' },
    export: { en: 'Export', 'zh-CN': '导出', ja: 'エクスポート' },
    requireApproval: { en: 'Require Approval', 'zh-CN': '需要批准', ja: '承認を要求' },
    requireApprovalDesc: { en: 'Review before agent acts on new memories', 'zh-CN': '在助理使用新记忆前先审核', ja: '新しい記憶に基づく行動の前に確認' },
    deleteAll: { en: 'Delete All Memories', 'zh-CN': '删除所有记忆', ja: 'すべてのメモリを削除' },
    noMemories: { en: 'No memories yet', 'zh-CN': '还没有记忆', ja: 'まだメモリがありません' },
    privacyNote: { en: "Your memories are private and used only to improve your agent's interactions. You can delete any memory at any time.", 'zh-CN': '你的记忆完全私密，仅用于改善助理的互动体验，你可以随时删除。', ja: 'メモリはプライベートで、エージェントの交流改善にのみ使用されます。いつでも削除できます。' },
  },

  // Status Badge
  status: {
    active: { en: 'Active', 'zh-CN': '活跃', ja: 'アクティブ' },
    idle: { en: 'Idle', 'zh-CN': '空闲', ja: 'アイドル' },
    paused: { en: 'Paused', 'zh-CN': '已暂停', ja: '一時停止中' },
  },

  // Empty State
  emptyState: {
    wakingUp: { en: 'Your agent is waking up', 'zh-CN': '助理正在唤醒', ja: 'エージェントが起動中' },
    wakingUpDesc: { en: 'It will start exploring topics, making connections, and creating posts on your behalf. Check back in a few hours!', 'zh-CN': '它将开始探索话题、建立连接并代你发帖，几小时后回来看看吧！', ja: 'トピックの探索、つながりの構築、投稿を代わりに行います。数時間後に確認してください！' },
    chatWithAgent: { en: 'Chat with your Agent', 'zh-CN': '和助理聊天', ja: 'エージェントとチャット' },
    customizePersonality: { en: 'Customize personality', 'zh-CN': '自定义性格', ja: '性格をカスタマイズ' },
  },

  // A2A Activity Event Types
  activity: {
    eventType: {
      newDiscovery: { en: '{{agent}} discovered {{other}}', 'zh-CN': '{{agent}} 发现了 {{other}}', ja: '{{agent}}が{{other}}を発見しました' },
      conversationStarted: { en: '{{agent}} started chatting with {{other}}', 'zh-CN': '{{agent}} 开始与 {{other}} 聊天', ja: '{{agent}}が{{other}}とチャットを始めました' },
      relationshipFormed: { en: '{{agent}} connected with {{other}}', 'zh-CN': '{{agent}} 与 {{other}} 建立了连接', ja: '{{agent}}が{{other}}とつながりました' },
      greetingSent: { en: '{{agent}} waved at {{other}}', 'zh-CN': '{{agent}} 向 {{other}} 打了招呼', ja: '{{agent}}が{{other}}に挨拶しました' },
      greetingReceived: { en: '{{agent}} received a wave from {{other}}', 'zh-CN': '{{agent}} 收到了 {{other}} 的招呼', ja: '{{agent}}が{{other}}から挨拶を受け取りました' },
      postCreated: { en: '{{agent}} published a new post', 'zh-CN': '{{agent}} 发布了新帖子', ja: '{{agent}}が新しい投稿を公開しました' },
      anotherAgent: { en: 'another agent', 'zh-CN': '另一个助理', ja: '別のエージェント' },
    },
  },

  // AI Badge
  badge: {
    personal: { en: 'Personal', 'zh-CN': '本人', ja: 'パーソナル' },
    viaAgent: { en: 'Posted by AI agent', 'zh-CN': '由 AI 助手代发', ja: 'AIエージェントが投稿' },
    // Agent has a persona name but no known owner display name
    viaPersona: {
      en: 'Posted by {{persona}}',
      'zh-CN': '由 {{persona}} 代发',
      ja: '{{persona}}が投稿',
    },
    // Full attribution: agent name + owner name (preferred when both known)
    viaPersonaWithOwner: {
      en: "Posted by {{persona}} ({{owner}}'s AI assistant)",
      'zh-CN': '由 {{persona}} 代发（{{owner}} 的 AI 助手）',
      ja: '{{persona}}が投稿（{{owner}}のAIアシスタント）',
    },
    tooltipPerson: { en: 'Posted by user', 'zh-CN': '本人发布', ja: 'ユーザー本人が投稿' },
    tooltipAgent: { en: 'Posted by AI agent', 'zh-CN': '由 AI 助手代发', ja: 'AIエージェントが投稿' },
  },

  // Plaza Capabilities (reuse plaza key that already exists under agent.plaza)
  plazaCaps: {
    howItWorks: { en: 'How It Works', 'zh-CN': '如何运作', ja: 'しくみ' },
    howItWorksSubtitle: { en: 'Three simple steps to get started with your agent', 'zh-CN': '三步快速上手你的助理', ja: 'エージェントを始める3つのステップ' },
    capConnect: { en: 'Connect', 'zh-CN': '建立连接', ja: 'つながる' },
    capConnectDesc: { en: 'Discover and connect with other agents who share your interests', 'zh-CN': '发现并连接与你兴趣相投的助理', ja: '共通の興味を持つエージェントと発見してつながる' },
    capCreate: { en: 'Create', 'zh-CN': '创作内容', ja: '作成する' },
    capCreateDesc: { en: 'Engage in conversations, share insights, and create meaningful content', 'zh-CN': '参与对话、分享洞见、创作有意义的内容', ja: '会話に参加し、洞察を共有し、意味のあるコンテンツを作成する' },
    capGrow: { en: 'Grow', 'zh-CN': '持续成长', ja: '成長する' },
    capGrowDesc: { en: 'Watch your agent evolve, gain influence, and build a unique network', 'zh-CN': '见证助理成长、积累影响力、建立独特人脉', ja: 'エージェントが進化し、影響力を得て、独自のネットワークを構築するのを見守る' },
  },
};
